import defs from "./stepDefinitions.json";

type StepDomain = "processing" | "syndication" | "storage";
type StepRenderer = "dynamic" | "component";
type FieldType = "string" | "number" | "boolean" | "select";
type RuleKind =
  | "vumoApiEndpointFromProfile"
  | "vumoApiEndpointFromProfileManual"
  | "vumoApiEndpointFromProfilePano"
  | "brightRiverCallback"
  | "constant"
  | "constantIfMissing";

export interface StepFieldOption {
  label: string;
  value: string;
}

export interface StepFieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  inputType?: string;
  disabled?: boolean;
  hidden?: boolean;
  inputMaxLength?: number;
  options?: StepFieldOption[];
  visibleWhen?: {
    field: string;
    equals: string | number | boolean;
  };
}

export interface StepFlagDefinition {
  key: string;
  excludeIfEnabled?: string[];
}

export interface StepDerivedRule {
  target: string;
  ruleName?: RuleKind;
  kind?: RuleKind;
  dependsOn: string[];
  value?: any;
}

export interface StepDefinition {
  id: string;
  service_description: string;
  service_flag: string[];
  service_category: StepDomain;
  label: string;
  order: number;
  renderer: StepRenderer;
  stepFlags: StepFlagDefinition[];
  defaults?: Record<string, any>;
  fields?: StepFieldDefinition[];
  autoPopulateRules?: StepDerivedRule[];
  derivedRules?: StepDerivedRule[];
}

interface StepDefsPayload {
  services: Record<string, any>;
}

const payload = defs as StepDefsPayload;

export const getStepDefinitions = (domain: StepDomain): StepDefinition[] => {
  return Object.entries(payload.services)
    .filter(([, def]) => (def.service_category || def.category) === domain)
    .map(([id, def]) => ({
      id,
      service_description: def.service_description || def.label || id,
      service_flag: Array.isArray(def.service_flag)
        ? def.service_flag
        : Array.isArray(def.stepFlags)
          ? def.stepFlags.map((f: any) => f.key).filter(Boolean)
          : [],
      service_category: def.service_category || def.category,
      label: def.label || def.service_description || id,
      order: def.order,
      renderer: def.renderer,
      stepFlags: def.stepFlags,
      defaults: def.defaults,
      fields: def.fields,
      autoPopulateRules: def.autoPopulateRules || def.derivedRules,
      derivedRules: def.derivedRules,
    }))
    .sort((a, b) => a.order - b.order);
};

export const getStepDefinitionMap = (domain: StepDomain): Record<string, StepDefinition> => {
  return getStepDefinitions(domain).reduce<Record<string, StepDefinition>>((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
};

export const applyDerivedRule = (
  rule: StepDerivedRule,
  config: Record<string, any>
): any => {
  const ruleName = rule.ruleName;
  switch (ruleName) {
    case "vumoApiEndpointFromProfile": {
      if (config.processor !== "vumo") return config.apiEndpoint || "";
      const profile = String(config.profile || "").trim();
      if (!profile) return "";
      if (profile === "platform_30_off_roll_compensation_car_crop_09") {
        return "https://api.vumography.vumo.ai/api/v1/process/single/platform_30_off_roll_compensation_car_crop_09, https://api.vumography.vumo.ai/api/v1/process/single/platform_30_static_image";
      }
      return `https://api.vumography.vumo.ai/api/v1/process/single/${profile}`;
    }
    case "vumoApiEndpointFromProfileManual": {
      const profile = String(config.profile || "default_detail").trim();
      if (!profile) return "";
      if (profile === "platform_30_off_roll_compensation_car_crop_09") {
        return "https://api.vumography.vumo.ai/api/v1/process/single/platform_30_off_roll_compensation_car_crop_09, https://api.vumography.vumo.ai/api/v1/process/single/platform_30_static_image";
      }
      return `https://api.vumography.vumo.ai/api/v1/process/single/${profile}`;
    }
    case "vumoApiEndpointFromProfilePano": {
      const profile = String(config.profile || "default_interior_simple").trim();
      if (!profile) return "";
      return `https://api.vumography.vumo.ai/api/v1/process/single/${profile}`;
    }
    case "brightRiverCallback":
      return config.processor === "br"
        ? "www.bwiportal.com/br/completed"
        : config.callBackURL || "";
    case "constant":
      return rule.value as string;
    case "constantIfMissing":
      return (config[rule.target] ?? rule.value) as string;
    default:
      return "";
  }
};
